<template>
  <div :id="contentId" class="map">
    <div class="loading-overlay" v-if="isLoading">
      <img src="/frontend/img/loading-spinner.svg" />
    </div>
    <l-map v-if="ready"
      ref="map"
      :center="center"
      :zoom="zoom"
      map-type-id="terrain"
      class="full-screen"
    >
      <v-tilelayer-google-maps
        :apikey="apiKey"
        :options="googleMutantOptions"
      />

      <v-marker
        v-if="hasPlottableMyGeometry"
        :lat-lng="myGeometryLatLng"
        :clickable="true"
        :icon="myGeometryIcon"
      />

      <v-marker-cluster
        :key="'cluster-' + clusterEpoch"
        :options="clusterOptions"
        ref="clusterRef"
      >
        <v-marker
          v-for="m in plottableMarkers"
          v-bind:key="m.depot.id + '-' + m.depot.marker_color_key"
          :lat-lng="m.latLng"
          :clickable="true"
          @click="onMarkerPress(m.depot)"
          :options="m.options"
          :icon="m.icon"
        />
      </v-marker-cluster>
    </l-map>

    <div class="legend" v-if="showLegend">
      <div class="arrow">
        <i class="cs-arrow-right"></i>
      </div>
      <label>{{ i18n.app.map.legend }}</label>
      <ul>
        <li
          v-for="markerColor in markerColors"
          :class="markerColor.key"
          v-bind:key="markerColor.key"
        >
          <span></span>
          {{ markerColor.text }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import L, { DivIcon } from "leaflet";
import "@/plugin/leaflet/safeLatLngBounds";
import { LMap, LTileLayer, LMarker, LIcon } from "vue2-leaflet";
import Vue2LeafletGoogleMutant from "@/plugin/leaflet/Vue2LeafletGoogleMutant";
import Vue2LeafletMarkerCluster from "vue2-leaflet-markercluster";
import MapConfig from "./MapConfig";
import { Appearance } from "@/models/UserPreference";
import { MarkerColorKey } from "@/models/MarkerColor";
import { mapGetters } from "vuex";
import Depot from "@/models/Depot";
import { loadGoogleMapsScript } from "@/utils/googleMapsLoader";
import { MapState } from "@/helpers/MapState";

export { MapState };

if (L.MarkerCluster && !L.MarkerCluster._rrPatched) {
  L.MarkerCluster._rrPatched = true;

  const zoomToBounds = L.MarkerCluster.prototype.zoomToBounds;
  L.MarkerCluster.prototype.zoomToBounds = function(fitBoundsOptions) {
    const bounds = this._bounds;
    const sw = bounds?._southWest;
    const ne = bounds?._northEast;
    const finite =
      bounds &&
      Number.isFinite(sw?.lat) &&
      Number.isFinite(sw?.lng) &&
      Number.isFinite(ne?.lat) &&
      Number.isFinite(ne?.lng);
    const ll = this._latlng;
    const latlngOk =
      ll && Number.isFinite(ll.lat) && Number.isFinite(ll.lng);

    if (!finite) {
      const map = this._group?._map;
      if (map && latlngOk) {
        map.setView(ll, map.getZoom() + 1);
      }
      return;
    }
    return zoomToBounds.call(this, fitBoundsOptions);
  };
}

const MAX_ZOOM = 13;
export default {
  props: {
    models: Object | Array,
    clusterEpoch: {
      type: Number,
      default: 0
    },
    markerColors: Array,
    myGeometry: Object,
    isLoading: Boolean,
    showLegend: Boolean,
    contentId: {
      type: String,
      default: "content"
    }
  },
  components: {
    "l-map": LMap,
    "v-marker": LMarker,
    "v-tilelayer": LTileLayer,
    "v-icon": LIcon,
    "v-tilelayer-google-maps": Vue2LeafletGoogleMutant,
    "v-marker-cluster": Vue2LeafletMarkerCluster
  },
  data() {
    return {
      MapConfig: MapConfig,
      apiKey: env.GOOGLE_API_KEY_FRONTEND,
      zoom: 9,
      center: [52.5, 5.5],
      Appearance: Appearance,
      bounds: null,
      MarkerColorKey: MarkerColorKey,
      selectedDepot: null,
      ready: false,
      myGeometryIcon: new DivIcon({
        className: "my-div-icon",
        iconSize: [24, 30.5],
        html: `<div class="cs-marker me"></div>`
      }),
      clusterOptions: null
    };
  },
  created() {
    this._markerCache = Object.create(null);
    this.clusterOptions = {
      iconCreateFunction: this.getClusterIcon,
      maxClusterRadius: 60,
      /* 11 grouped depots up to ~5km apart, so a country view was all clusters. */
      disableClusteringAtZoom: 8,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false
    };
  },
  mounted() {
    $(".legend").click(function() {
      $(this).toggleClass("open", 1000);
    });

    loadGoogleMapsScript(env.GOOGLE_API_KEY_FRONTEND)
      .then(() => {
        this.ready = true;
      })
      .catch(error => console.error("Google Maps failed to load", error));
  },
  watch: {
    plottableClusterSignature() {
      this.$nextTick(() => this.refreshClusters());
    },
    ready(isReady) {
      if (isReady) this.$nextTick(() => this.refreshClusters());
    }
  },
  beforeDestroy() {
    const bounds = this.getBounds();
    const sw = bounds?.getSouthWest?.() ?? bounds?._southWest;
    const ne = bounds?.getNorthEast?.() ?? bounds?._northEast;

    if (
      Depot.isFiniteLatLng(sw?.lat, sw?.lng) &&
      Depot.isFiniteLatLng(ne?.lat, ne?.lng)
    ) {
      MapState.save(bounds, null, []);
    }

    this.detachLeafletLayers();
  },
  methods: {
    detachLeafletLayers() {
      const map = this.$refs.map?.mapObject;
      if (!map || typeof map.eachLayer !== "function") return;

      const layers = [];
      map.eachLayer(layer => layers.push(layer));
      layers.forEach(layer => {
        try {
          map.removeLayer(layer);
        } catch (e) {
          if (map._layers && layer?._leaflet_id) {
            delete map._layers[layer._leaflet_id];
          }
          if (layer) layer._map = null;
        }
      });
    },
    highlightMarkers(models_focused) {
      this.$forceUpdate();
    },
    dropClusterFromMap() {
      const cluster = this.$refs.clusterRef?.mapObject;
      const map = this.$refs.map?.mapObject;
      if (!cluster) return;

      try {
        cluster.clearLayers();
      } catch (e) {}

      if (map && typeof map.hasLayer === "function" && map.hasLayer(cluster)) {
        try {
          map.removeLayer(cluster);
        } catch (e) {}
      }
    },
    refreshClusters() {
      const cluster = this.$refs.clusterRef?.mapObject;
      if (cluster && typeof cluster.refreshClusters === "function") {
        cluster.refreshClusters();
      }
    },
    onMarkerPress(depot) {
      if (!Depot.hasCoordinates(depot)) return;

      this.selectedDepot = depot;

      this.panTo({
        lat: Number(depot.address.latitude),
        lng: Number(depot.address.longitude)
      });

      this.$emit("onMarkerPress", depot);
    },
    depotMarkerKey(depot) {
      const selected = !!(
        depot?.state?.selected ||
        (this.selectedDepot && this.selectedDepot.id === depot.id)
      );
      return [
        depot.id,
        Number(depot.address.latitude),
        Number(depot.address.longitude),
        this.getVehicleCount(depot),
        this.getDepotIconColor(depot),
        selected,
        depot?.state?.focused !== false,
        this.appearance
      ].join("|");
    },
    markerBindingFor(depot) {
      if (!this._markerCache) this._markerCache = Object.create(null);
      const key = this.depotMarkerKey(depot);
      const prev = this._markerCache[depot.id];
      if (prev && prev.key === key) return prev;

      const lat = Number(depot.address.latitude);
      const lng = Number(depot.address.longitude);
      const count = this.getVehicleCount(depot);
      const binding = {
        key,
        depot,
        latLng:
          prev && prev.latLng[0] === lat && prev.latLng[1] === lng
            ? prev.latLng
            : [lat, lng],
        options:
          prev && prev.options.vehicle_count === count
            ? prev.options
            : { vehicle_count: count },
        icon: new DivIcon({
          className: "my-div-icon",
          iconSize: [24, 30.5],
          html: `
        <div class="cs-marker ${this.getDepotIconColor(depot)} ${
            depot?.state?.selected ||
            (this.selectedDepot && this.selectedDepot.id === depot.id)
              ? "animated bounce"
              : ""
          } ${
            depot?.state?.focused === false && depot?.state?.selected === false
              ? "empty"
              : ""
          }">
          ${this.getVehicleCountText(depot)}
        </div>`
        })
      };
      this._markerCache[depot.id] = binding;
      return binding;
    },
    getClusterIcon(cluster) {
      const markers = cluster.getAllChildMarkers();
      var count = 0;

      markers.map(item => (count += item.options.vehicle_count));

      return new DivIcon({
        html: `
        <div class="cs-cluster blue ${count == 0 ? "empty" : ""}">
          <span>${count}</span>
        </div>`,
        className: "marker-cluster",
        iconSize: [40, 40]
      });
    },
    panTo(coords, userZoom) {
      if (!this.$refs.map || !this.$refs.map.mapObject) {
        return;
      }

      const lat = Number(coords?.lat ?? coords?.[0]);
      const lng = Number(coords?.lng ?? coords?.[1]);
      if (!Depot.isFiniteLatLng(lat, lng)) return;

      const point = { lat, lng };
      const minZoom = userZoom || 10;
      const zoom = this.$refs.map.mapObject.getZoom();

      if (zoom < minZoom) {
        this.$refs.map.mapObject.flyTo(point, minZoom, {
          animated: true,
          easeLinearity: 1,
          duration: 0.75
        });
      } else {
        this.$refs.map.mapObject.panTo(point, { animated: true });
      }
    },
    fitBounds(latlngArr = []) {
      if (!this.$refs?.map?.mapObject) return;

      const points = (Array.isArray(latlngArr) ? latlngArr : []).filter(p =>
        Array.isArray(p)
          ? Depot.isFiniteLatLng(p[0], p[1])
          : Depot.isFiniteLatLng(p?.lat, p?.lng)
      );

      if (!points.length) return;

      return this.$refs.map.mapObject.fitBounds(points, {
        animate: false,
        maxZoom: MAX_ZOOM
      });
    },
    getBounds() {
      if (!this.$refs.map?.mapObject) return;

      return this.$refs?.map?.mapObject?.getBounds();
    },
    getCenter(coords) {
      return this.$refs?.map?.mapObject?.getCenter?.();
    },
    getVehicleCount(m) {
      if (!m?.state) return 1;
      return m?.state?.available_vehicles
        ? m?.state?.available_vehicles?.length
        : 0;
    },
    getVehicleCountText(m) {
      return m?.state
        ? `<span>${m?.state?.available_vehicles?.length}</span>`
        : "";
    },
    getDepotIconColor(depot) {
      if (this.selectedDepot && this.selectedDepot.id === depot.id)
        return MarkerColorKey.RED;
      if (!depot?.state) return "blue";
      return depot?.state?.available_vehicles?.length > 0
        ? depot.marker_color?.key || "blue"
        : MarkerColorKey.RED;
    }
  },
  computed: {
    ...mapGetters({
      appearance: "appearance"
    }),
    plottableModels() {
      if (!this.models) return [];

      const list = Array.isArray(this.models)
        ? this.models
        : Object.values(this.models);

      return list.filter(model => Depot.hasCoordinates(model));
    },
    plottableMarkers() {
      return this.plottableModels.map(depot => this.markerBindingFor(depot));
    },
    plottableClusterSignature() {
      return this.plottableMarkers
        .map(m => m.depot.id + ":" + m.options.vehicle_count)
        .join(",");
    },
    googleMutantOptions() {
      return {
        type: "roadmap",
        styles: MapConfig[(this.appearance || "").toLowerCase()]
      };
    },
    myGeometryLatLng() {
      return [
        Number(this.myGeometry.location.lat),
        Number(this.myGeometry.location.lng)
      ];
    },
    hasPlottableMyGeometry() {
      return Depot.isFiniteLatLng(
        this.myGeometry?.location?.lat,
        this.myGeometry?.location?.lng
      );
    }
  }
};
</script>

<style scoped>
@import "~leaflet.markercluster/dist/MarkerCluster.css";
@import "~leaflet.markercluster/dist/MarkerCluster.Default.css";
</style>
